import { z } from 'zod';
import {
    ColumnElementSchema,
    type ColumnElementSelectType,
    type ColumnElementType,
    OptionSchema,
    type OptionType,
    SectionSchema,
    type SectionType,
} from './types';

export class SelectManager {
    private elements: ColumnElementType[] = [];
    private options: OptionType[] = [];
    private sections: SectionType[] = [];
    private _isSet: boolean = false;

    set(elements: ColumnElementType[], options: OptionType[], sections: SectionType[]): this {
        this.elements = z.array(ColumnElementSchema).parse(elements); // rewrite due to import variable being read-only (?)
        this.options = z.array(OptionSchema).parse(options); // rewrite due to import variable being read-only (?)
        this.sections = z.array(SectionSchema).parse(sections); // rewrite due to import variable being read-only (?)
        this._isSet = true;
        return this;
    }

    isSet(): boolean {
        return this._isSet;
    }

    getElements(): ColumnElementType[] {
        return this.elements;
    }

    getOptions(): OptionType[] {
        return this.options;
    }

    getSections(): SectionType[] {
        return this.sections;
    }

    getElementsFromSection(sectionId: string): ColumnElementType[] {
        let elements = this.elements.filter(element => element.section === sectionId);
        return elements;
    }

    updateStreetWarningDependsOnHouseNumberSelection() {
        let streetElement = this.getElementById('street') as ColumnElementSelectType | undefined;
        let houseNumberElement = this.getElementById('house') as
            | ColumnElementSelectType
            | undefined;
        // if houseNumberElement is selected, set visibleWarning on streetElement to false
        if (streetElement && houseNumberElement) {
            streetElement.visibleWarning = houseNumberElement.selectedOptionId === '';
        }
    }

    getElementById(id: string): ColumnElementType | undefined {
        return this.elements.find(element => element.id === id);
    }

    checkWarningsAndErrors(): boolean {
        let valid = true;
        this.elements.forEach(element => {
            if (element.type === 'select') {
                element.error = '';
                element.warning = '';
                if (
                    element.optionsErrors.filter(err => err.id === element.selectedOptionId)
                        .length > 0
                ) {
                    valid = false;
                    element.error =
                        element.optionsErrors.find(err => err.id === element.selectedOptionId)
                            ?.message || '';
                }
                if (
                    element.optionsWarnings.filter(warn => warn.id === element.selectedOptionId)
                        .length > 0
                ) {
                    element.warning =
                        element.optionsWarnings.find(warn => warn.id === element.selectedOptionId)
                            ?.message || '';
                }
                if (element.selectedOptionId === '' && element.required) {
                    valid = false;
                    element.error = 'To pole jest wymagane. Wybierz odpowiednią opcję.'; // TODO: move it to translation as argument of component
                }
            }
        });
        return valid;
    }

    reloadOptions(): this {
        this.elements.forEach(el => {
            if (el.type === 'select') this.setSelectOption(el.id, el.selectedOptionId);
        });
        return this;
    }

    setSelectOption(elementId: string, optionId: string) {
        // console.log('SVELTE: setSelectOption', elementId, optionId);
        let opts = optionId.split(',');
        // set '' where optionId is already set
        this.elements.forEach(element => {
            if (element.type === 'select') {
                let elemopts = element.selectedOptionId.split(',');
                opts.forEach(opt => {
                    if (elemopts.indexOf(opt) >= 0) {
                        elemopts.splice(elemopts.indexOf(opt), 1);
                    }
                });
                element.selectedOptionId = elemopts.join(',');
            }
        });
        const element = this.elements.find(element => element.id === elementId);
        if (element && element.type === 'select') {
            element.selectedOptionId = optionId;
            let checkboxes = this.elements.filter(element => element.type == 'checkbox');
            let selects = this.elements.filter(element => element.type == 'select');
            checkboxes.forEach(checkbox => {
                let checked: boolean | null = null;
                let disabled: boolean = false;
                // disabled -- gdy dana kolumna jest przypisana
                // checked -- gdy dana kolumna jest przypisana do pola, które ma visible=true
                selects.forEach(select => {
                    if (select.type === 'select') {
                        if (
                            select.selectedOptionId != '' &&
                            select.selectedOptionId.split(',').indexOf(checkbox.id) >= 0
                        ) {
                            // console.debug(
                            //     'ColumnSelectManager: setOption - checkbox',
                            //     checkbox.id,
                            //     'enabled',
                            //     select.visible,
                            //     'select',
                            //     select,
                            //     'select.selectedOptionId',
                            //     select.selectedOptionId
                            // );
                            disabled = typeof select.visible == 'boolean'; // this.isRequiredCheckboxForDriver(select);
                            checked = this.isRequiredCheckboxForDriver(select);
                        }
                    }
                });
                if (checkbox.type === 'checkbox') {
                    if (checked !== null) {
                        checkbox.checked = checked;
                    }
                    checkbox.enabled = !disabled ?? false;
                }
                // console.debug('ColumnSelectManager: setOption - checkbox', checkbox);
            });
        }
    }

    setCheckboxOption(elementId: string, checked: boolean, order: number | undefined) {
        const element = this.elements.find(element => element.id === elementId);
        if (element && element.type === 'checkbox') {
            element.checked = checked;
            element.order = order;
        }
    }

    getUsedOptions(): string[] {
        let optionsSelected: string[] = [];
        this.elements.forEach(element => {
            if (element.type === 'select' && element.selectedOptionId !== '') {
                optionsSelected.push(element.selectedOptionId);
            }
            if (element.type === 'select' && element.multi && element.selectedOptionId !== '') {
                optionsSelected.push(...element.selectedOptionId.split(','));
            }
        });
        return optionsSelected;
    }

    getUsedOptionsNames(): string[] {
        console.log('getUsedOptionsNames', this.elements);
        let usedNames: string[] = [];
        this.elements.forEach(el => {
            if (el.type === 'select') {
                if (el.selectedOptionId === '') return;
                usedNames.push(el.id);
            }
        });
        return usedNames;
    }

    isRequiredCheckboxForDriver(select: ColumnElementSelectType): boolean {
        // const requiredCheckboxes = ['street', 'city', 'pcode', 'house'];
        // return requiredCheckboxes.includes(selectId);
        return !!select.visible;
    }
}
